import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatPrice } from '../utils/currencyUtils';
import './Cart.css';

function Cart() {
  const { items, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const { currency } = useCurrency();
  const [forceUpdate, setForceUpdate] = useState(0);

  const handleQuantityChange = (item, newQuantity) => {
    updateQuantity(item.id, parseInt(newQuantity));
  };

  const handleRemoveItem = (item) => {
    console.log('Cart.js: Removing item:', item);
    removeFromCart(item);
    // Force a re-render to ensure UI updates
    setForceUpdate(prev => prev + 1);
  };

  // Monitor cart changes
  useEffect(() => {
    console.log('Cart items changed:', items);
  }, [items, forceUpdate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart-container">
          <div className="empty-cart-content">
            <div className="empty-cart-icon"></div>
            <h1>Your Cart is Empty</h1>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <p>Start shopping to discover amazing products!</p>
            <Link to="/products" className="start-shopping-btn">
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-hero">
        <div className="cart-hero-content">
          <h1>Your Shopping Cart</h1>
          <p>{getCartCount()} items in your cart</p>
        </div>
      </div>

      <div className="cart-main">
        <div className="cart-container">
          <div className="cart-items-section">
            <div className="cart-items-header">
              <h2>Cart Items</h2>
              <span className="items-count">{getCartCount()} items</span>
            </div>
            
            <div className="cart-items-list">
              {items.map((item, index) => (
                <div key={`${item.id}-${item.variation?.combination || 'no-variation'}-${index}`} className="cart-item-card">
                  <div className="item-image-section">
                    <div className="item-image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                      <span className="item-emoji">Box</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="item-info-section">
                    <div className="item-details">
                      <h3 className="item-name">{item.name}</h3>
                      {item.variation && (
                        <div className="item-variation">
                          <span className="variation-label">Variation:</span>
                          <span className="variation-value">{item.variation.combination}</span>
                        </div>
                      )}
                      <p className="item-description">{item.description || 'Amazing product for you!'}</p>
                      <div className="item-price-display">
                        <span className="current-price">{formatPrice(item.price || 0, currency)}</span>
                        {item.originalPrice && item.originalPrice > item.price && (
                          <>
                            <span className="original-price">{formatPrice(item.originalPrice, currency)}</span>
                            <span className="discount-badge">
                              {Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}% OFF
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="item-actions-section">
                    <div className="quantity-controls">
                      <label className="quantity-label">Quantity</label>
                      <div className="quantity-selector">
                        <select
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item, e.target.value)}
                          className="quantity-select"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="item-total-section">
                      <span className="item-total-label">Total</span>
                      <span className="item-total-price">{formatPrice((item.price || 0) * item.quantity, currency)}</span>
                    </div>

                    <button
                      className="remove-item-button"
                      onClick={() => handleRemoveItem(item)}
                      title="Remove item"
                    >
                      <span className="remove-icon">×</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="cart-summary-section">
            <div className="cart-summary-card">
              <div className="summary-header">
                <h3>Order Summary</h3>
                <div className="summary-icon">Summary</div>
              </div>
              
              <div className="summary-details">
                <div className="summary-row">
                  <span>Subtotal ({getCartCount()} items)</span>
                  <span>{formatPrice(getCartTotal(), currency)}</span>
                </div>
                
                <div className="summary-divider"></div>
                
                <div className="summary-row total-row">
                  <span>Total</span>
                  <span className="total-amount">{formatPrice(getCartTotal(), currency)}</span>
                </div>
              </div>

              <div className="summary-actions">
                <Link to="/checkout" className="checkout-button">
                  <span className="checkout-icon">→</span>
                  Proceed to Checkout
                </Link>
                
                <Link to="/products" className="continue-shopping-button">
                  <span className="shopping-icon">←</span>
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart; 